/**
 * AdminVoices (#557, tab refactor + Deepgram STT model picker) — admin
 * CRUD over the per-provider voice catalogues, plus a Deepgram-only STT
 * model selector for latency/quality experiments.
 *
 * Hybrid model: each TTS provider's hardcoded `_VOICE_CATALOGUE` is the
 * seed / safe fallback; the DB stores deltas (hide / rename / addition).
 * The Portal Companion voice picker reads through the same merged
 * catalogue, so an admin edit takes effect on the next family page-load
 * without a redeploy.
 *
 * Layout:
 *   - Tab bar across the top: one tab per provider (azure / elevenlabs /
 *     deepgram). Only the selected tab's content renders, keeping the
 *     page readable as catalogues grow.
 *   - Per tab:
 *     - Editable table of effective voices.
 *       - Hardcoded rows: edit display_name + region; toggle hidden;
 *         preview audio. Cannot delete (hidden=true is the soft-delete).
 *       - Addition rows: edit all fields; preview; delete (hard).
 *     - Add-new form per section.
 *   - Deepgram tab additionally surfaces the STT model picker. Default
 *     is `nova-2-conversationalai` (English-only but tuned for AI dialog
 *     turn-taking); the deepgram service transparently falls back to
 *     `nova-2` when the chosen model can't handle the call's language.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { adminGet, adminPost, adminPatch, adminDelete, adminPut } from './adminApi';

const PROVIDERS = ['azure', 'elevenlabs', 'deepgram'];
const GENDERS = ['female', 'male', 'neutral'];

const styles = `
  .av-toolbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; flex-wrap:wrap; gap:12px; }
  /* Tab bar — one tab per provider. Sits above the per-provider section
     and persists the selection so navigating between rows in a tab
     doesn't reset what you were looking at. */
  .av-tabs { display:flex; gap:4px; border-bottom:2px solid #E8E3DC; margin-bottom:24px; flex-wrap:wrap; }
  .av-tab { background:transparent; border:none; padding:10px 18px; font-size:0.95rem; font-weight:600; color:#888; cursor:pointer; font-family:inherit; border-bottom:3px solid transparent; margin-bottom:-2px; transition:color 0.15s; text-transform:capitalize; }
  .av-tab:hover { color:#2C2C2C; }
  .av-tab.active { color:#E8845A; border-bottom-color:#E8845A; }
  .av-tab .av-tab-count { color:#999; font-weight:400; font-size:0.82rem; margin-left:6px; }
  .av-section { margin-bottom:36px; }
  .av-section h2 { font-size:1.05rem; font-weight:700; color:#2C2C2C; margin:0 0 12px; text-transform:capitalize; }
  /* Scroll wrapper — caps each provider section at ~10 visible rows so
     long catalogues (Azure has 17+) don't push the rest of the page
     out of reach. The chrome (border + radius) lives here so the
     sticky thead doesn't visually break the rounded corners. */
  .av-table-scroll { max-height:480px; overflow-y:auto; background:#fff; border:1.5px solid #E8E3DC; border-radius:14px; }
  .av-table { width:100%; border-collapse:collapse; }
  .av-table th, .av-table td { text-align:left; padding:10px 12px; font-size:0.88rem; vertical-align:middle; }
  /* Sticky thead so column labels stay readable while scrolling.
     box-shadow under the row replaces the border-bottom that would
     otherwise scroll out of position with the row. */
  .av-table thead th { position:sticky; top:0; z-index:1; background:#FAFAF8; box-shadow:inset 0 -1.5px 0 #E8E3DC; font-size:0.78rem; text-transform:uppercase; letter-spacing:0.04em; color:#888; font-weight:700; }
  .av-table tbody tr { border-bottom:1px solid #F0EBE5; }
  .av-table tbody tr:last-child { border-bottom:none; }
  .av-table tbody tr.hidden-row { opacity:0.55; background:#FAFAF8; }
  .av-input { border:1.5px solid #E0D8D0; border-radius:8px; padding:6px 10px; font-size:0.85rem; font-family:inherit; color:#2C2C2C; background:#FAFAF8; width:100%; min-width:90px; box-sizing:border-box; }
  .av-input:focus { outline:none; border-color:#E8845A; background:#fff; }
  .av-input.dirty { border-color:#E8845A; background:#FFF7F2; }
  .av-id { font-family:'SF Mono', 'Consolas', monospace; font-size:0.78rem; color:#666; word-break:break-all; }
  .av-source-pill { display:inline-block; font-size:0.7rem; font-weight:700; padding:2px 8px; border-radius:20px; }
  .av-source-pill.hardcoded { background:#EAEAEA; color:#666; }
  .av-source-pill.addition { background:#E8F3FF; color:#1A6BB8; }
  .av-actions { display:flex; gap:6px; flex-wrap:wrap; }
  .av-btn { background:#F5F0EB; color:#2C2C2C; border:1.5px solid #E0D8D0; border-radius:8px; padding:5px 10px; font-size:0.78rem; font-weight:600; cursor:pointer; font-family:inherit; transition:all 0.15s; white-space:nowrap; }
  .av-btn:hover { background:#EDE7E0; }
  .av-btn:disabled { opacity:0.5; cursor:not-allowed; }
  .av-btn-primary { background:#E8845A; color:#fff; border-color:#E8845A; }
  .av-btn-primary:hover { background:#D4724A; }
  .av-btn-danger { background:#fff; color:#C03030; border-color:#F0CFCF; }
  .av-btn-danger:hover { background:#FFF3F3; }
  .av-add { background:#fff; border:1.5px dashed #E0D8D0; border-radius:14px; padding:14px 18px; margin-top:12px; }
  .av-add h3 { font-size:0.85rem; text-transform:uppercase; letter-spacing:0.05em; color:#888; margin:0 0 10px; font-weight:700; }
  .av-add-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:8px; align-items:end; }
  .av-add-field label { display:block; font-size:0.72rem; font-weight:600; color:#888; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:4px; }
  .av-toast { position:fixed; bottom:20px; left:50%; transform:translateX(-50%); padding:10px 22px; border-radius:10px; font-size:0.9rem; font-weight:500; z-index:1000; }
  .av-toast.success { background:#22A062; color:#fff; }
  .av-toast.error { background:#C03030; color:#fff; }
  /* Deepgram STT model picker — sits above the voice catalogue so it's
     the first thing visible when an admin opens the Deepgram tab. */
  .av-stt-card { background:#fff; border:1.5px solid #E8E3DC; border-radius:14px; padding:18px 22px; margin-bottom:28px; }
  .av-stt-card h3 { font-size:0.95rem; font-weight:700; color:#2C2C2C; margin:0 0 6px; }
  .av-stt-card .av-stt-blurb { font-size:0.82rem; color:#666; margin-bottom:14px; }
  .av-stt-row { display:grid; grid-template-columns:130px 1fr 200px; gap:12px; align-items:center; margin-bottom:10px; }
  .av-stt-row label { font-size:0.78rem; font-weight:600; color:#555; text-transform:uppercase; letter-spacing:0.04em; }
  .av-stt-hint { font-size:0.75rem; color:#888; }
  .av-stt-pill { display:inline-block; font-size:0.7rem; font-weight:700; padding:2px 8px; border-radius:20px; margin-left:6px; }
  .av-stt-pill.override { background:#FFE9DC; color:#C04E18; }
  .av-stt-pill.default { background:#EAEAEA; color:#666; }
  .av-stt-coverage { font-size:0.75rem; color:#666; margin-top:8px; padding:8px 12px; background:#FAFAF8; border-radius:8px; }
  .av-stt-coverage code { font-family:'SF Mono', 'Consolas', monospace; font-size:0.72rem; color:#1A6BB8; }
  .av-stt-actions { display:flex; gap:10px; margin-top:12px; }
  .av-link-btn { background:transparent; border:none; color:#1A6BB8; padding:6px 4px; font-size:0.83rem; cursor:pointer; font-family:inherit; }
  .av-link-btn:hover { text-decoration:underline; }
  .av-link-btn:disabled { opacity:0.5; cursor:not-allowed; text-decoration:none; }

  @media (max-width: 700px) {
    .av-stt-row { grid-template-columns: 1fr; gap: 4px; }
    .av-stt-row label { margin-top: 8px; }
  }
`;

const emptyAdd = () => ({
  voice_id: '',
  display_name: '',
  language: '',
  gender: 'female',
  region: '',
});

// #1003 — the nova STT model picker is only live when Flux is OFF. When the
// audio-pipeline fingerprint (`ap`) reports Flux active
// (`nova_dropdown_active === false`), the picker is a dead knob (Flux overrides
// the nova model), so we render a status line instead of the editable control.
// `ap` null (fingerprint unavailable) → show the picker, so we never hide a
// genuinely working control.
export function novaPickerActive(ap) {
  return !ap || ap.nova_dropdown_active !== false;
}

// ── Deepgram STT model picker ───────────────────────────────────────────
// Lives in its own component so re-renders from the voice table don't
// disturb the dropdown state, and so the picker can manage its own
// load/save lifecycle independently.
function DeepgramSttPicker({ onToast }) {
  const [data, setData] = useState(null);
  const [ap, setAp] = useState(null);   // #985 — audio-pipeline fingerprint
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/stt-config');
      setData(res?.deepgram || null);
      setAp(res?.audio_pipeline || null);
      setModel((res?.deepgram?.override || res?.deepgram?.effective_model) || '');
    } catch (e) {
      onToast('error', e.message || 'Failed to load STT config');
    }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="av-stt-card">
        <p style={{ color: '#999', margin: 0 }}>Loading STT config…</p>
      </div>
    );
  }

  const isOverridden = data.override != null && data.override !== '';
  const dirty = model !== data.effective_model;
  const coverage = data.language_coverage[model] || 'all';
  const coverageLabel =
    coverage === 'all'
      ? 'all supported languages'
      : `only: ${coverage.join(', ')}`;
  const coverageWarn = coverage !== 'all';
  // #1003 — when Flux is the active engine the nova model picker is a dead
  // knob (Flux overrides it). Render a status line instead of the editable
  // dropdown + Save. See novaPickerActive() above.
  const novaActive = novaPickerActive(ap);

  async function save() {
    setBusy(true);
    try {
      await adminPut('/stt-config/deepgram', { model });
      onToast('success', `STT model set to ${model}`);
      await load();
    } catch (e) {
      onToast('error', e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    try {
      await adminPut('/stt-config/deepgram', { model: null });
      onToast('success', `Reset to default (${data.default_model})`);
      await load();
    } catch (e) {
      onToast('error', e.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="av-stt-card">
      <h3>Deepgram STT model</h3>
      {novaActive && (
        <p className="av-stt-blurb">
          Active model for both streaming WS and REST fallback paths. When
          the chosen model doesn't support a call's language, the service
          transparently falls back to <code>nova-2</code> for that call.
        </p>
      )}

      {/* #985/#1003 — when Flux is the active engine the nova model picker is a
          no-op, so we HIDE the editable dropdown + Save entirely (#1003) and
          show this status line instead of a dead knob under a warning. */}
      {!novaActive && (
        <div
          className="av-stt-coverage"
          style={{ background: '#FEF2F2', borderLeft: '3px solid #ef4444' }}
        >
          <strong>⚠️ Nova model selection is inactive.</strong> The audio
          pipeline is running the <code>{ap.engine}</code> engine
          {' '}(e.g. <code>flux-general-multi</code>), which overrides the nova
          model. The picker is hidden because changing it would have
          {' '}<strong>no effect</strong> on transcription until Flux is
          disabled (<code>FEATURE_DEEPGRAM_FLUX</code>).
        </div>
      )}

      {novaActive && (
      <div className="av-stt-row">
        <label>Model</label>
        <select
          className={`av-input ${dirty ? 'dirty' : ''}`}
          value={model}
          onChange={e => setModel(e.target.value)}
          disabled={busy}
        >
          {data.selectable_models.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
          {/* If override is something not in the selectable list (e.g. a
              variant typed in via API), still surface it so it's visible. */}
          {data.override && !data.selectable_models.includes(data.override) && (
            <option value={data.override}>{data.override} (custom)</option>
          )}
        </select>
        <div className="av-stt-hint">
          default: <code>{data.default_model}</code>
          {isOverridden
            ? <span className="av-stt-pill override">override</span>
            : <span className="av-stt-pill default">using default</span>}
        </div>
      </div>
      )}

      {novaActive && (
      <div
        className="av-stt-coverage"
        style={coverageWarn ? { background: '#FFF7F2', borderLeft: '3px solid #E8845A' } : null}
      >
        <strong>Language coverage:</strong> <code>{coverageLabel}</code>
        {coverageWarn && (
          <> · non-listed languages will fall back to <code>nova-2</code></>
        )}
      </div>
      )}

      {/* #985 — audio-pipeline fingerprint. The "same as before?" answer at a
          glance: a code version that matches the committed pin + when it last
          changed, the active engine, and the env-specific effective version. */}
      {ap && (
        <div
          className="av-stt-coverage"
          style={{ background: '#F8FAFC', borderLeft: '3px solid #64748b' }}
        >
          <strong>Audio pipeline:</strong> code <code>{ap.code_version}</code>
          {ap.code_version === ap.expected_code_version ? (
            <> · unchanged since {ap.code_unchanged_since}</>
          ) : (
            <span style={{ color: '#ef4444' }}>
              {' '}· ⚠ differs from committed <code>{ap.expected_code_version}</code>
            </span>
          )}
          {' · '}active engine <code>{ap.engine}</code>
          {' · '}effective <code>{ap.effective_version}</code>
        </div>
      )}

      {novaActive && (
      <div className="av-stt-actions">
        <button
          className="av-btn av-btn-primary"
          disabled={busy || !dirty}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {isOverridden && (
          <button className="av-link-btn" onClick={reset} disabled={busy}>
            Reset to default
          </button>
        )}
      </div>
      )}
    </div>
  );
}

export default function AdminVoices() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('azure');
  // Per-row dirty state (display_name / region edits awaiting Save).
  // Keyed by `${provider}::${voice_id}` so edits across providers
  // don't collide.
  const [edits, setEdits] = useState({});
  const [previewing, setPreviewing] = useState(null);
  const [adding, setAdding] = useState(() =>
    PROVIDERS.reduce((acc, p) => ({ ...acc, [p]: emptyAdd() }), {})
  );

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/voices');
      setData(res || {});
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load voices');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 2500);
  };

  const editKey = (provider, voiceId) => `${provider}::${voiceId}`;

  const onFieldChange = (provider, voice, field, value) => {
    const k = editKey(provider, voice.id);
    setEdits(prev => ({
      ...prev,
      [k]: { ...(prev[k] || {}), [field]: value },
    }));
  };

  const valueFor = (provider, voice, field) => {
    const k = editKey(provider, voice.id);
    if (edits[k] && edits[k][field] !== undefined) return edits[k][field];
    return voice[field] || '';
  };

  const isDirty = (provider, voiceId) => Boolean(edits[editKey(provider, voiceId)]);

  const onSave = async (provider, voice) => {
    const k = editKey(provider, voice.id);
    const patch = edits[k];
    if (!patch) return;
    setBusy(true);
    try {
      // Drop empty strings → server treats them as "no change" (the
      // override row uses NULL semantics for "inherit hardcoded").
      const body = {};
      for (const [field, val] of Object.entries(patch)) {
        if (val !== '') body[field] = val;
      }
      await adminPatch(`/voices/${provider}/${encodeURIComponent(voice.id)}`, body);
      setEdits(prev => { const { [k]: _, ...rest } = prev; return rest; });
      showToast('success', 'Saved');
      await load();
    } catch (e) {
      showToast('error', e.message || 'Save failed');
    }
    setBusy(false);
  };

  const onToggleHidden = async (provider, voice) => {
    setBusy(true);
    try {
      await adminPatch(`/voices/${provider}/${encodeURIComponent(voice.id)}`, {
        hidden: !voice.hidden,
      });
      showToast('success', voice.hidden ? 'Unhidden' : 'Hidden');
      await load();
    } catch (e) {
      showToast('error', e.message || 'Toggle failed');
    }
    setBusy(false);
  };

  const onDelete = async (provider, voice) => {
    if (!window.confirm(`Delete ${voice.name} (${voice.id})? This is permanent.`)) return;
    setBusy(true);
    try {
      await adminDelete(`/voices/${provider}/${encodeURIComponent(voice.id)}`);
      showToast('success', 'Deleted');
      await load();
    } catch (e) {
      showToast('error', e.message || 'Delete failed');
    }
    setBusy(false);
  };

  const onPreview = async (provider, voice) => {
    setPreviewing(editKey(provider, voice.id));
    try {
      const res = await fetch('/api/voices/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: voice.id,
          language: (voice.language || '').split('-')[0] || 'en',
          provider,
        }),
      });
      if (!res.ok) throw new Error('Preview failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPreviewing(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPreviewing(null); };
      audio.play();
    } catch (e) {
      showToast('error', e.message || 'Preview failed');
      setPreviewing(null);
    }
  };

  const onAdd = async (provider) => {
    const form = adding[provider];
    if (!form.voice_id.trim() || !form.display_name.trim() || !form.language.trim()) {
      showToast('error', 'voice_id, display_name, language are required');
      return;
    }
    setBusy(true);
    try {
      await adminPost('/voices', {
        provider,
        voice_id: form.voice_id.trim(),
        display_name: form.display_name.trim(),
        language: form.language.trim(),
        gender: form.gender,
        region: form.region.trim() || null,
      });
      setAdding(prev => ({ ...prev, [provider]: emptyAdd() }));
      showToast('success', 'Added');
      await load();
    } catch (e) {
      showToast('error', e.message || 'Add failed');
    }
    setBusy(false);
  };

  if (error) {
    return (
      <AdminLayout>
        <style>{styles}</style>
        <p style={{ color: '#C03030' }}>Error: {error}</p>
      </AdminLayout>
    );
  }
  if (!data) {
    return (
      <AdminLayout>
        <style>{styles}</style>
        <p style={{ color: '#999' }}>Loading voices…</p>
      </AdminLayout>
    );
  }

  const voices = data[activeTab] || [];
  const form = adding[activeTab];

  return (
    <AdminLayout>
      <style>{styles}</style>
      <div className="av-toolbar">
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Voices</h1>
        <span style={{ color: '#888', fontSize: '0.85rem' }}>
          Hardcoded entries can be renamed or hidden; admin-added entries can be edited or deleted.
        </span>
      </div>

      {/* Provider tab bar */}
      <div className="av-tabs" role="tablist">
        {PROVIDERS.map(p => {
          const count = (data[p] || []).length;
          return (
            <button
              key={p}
              role="tab"
              aria-selected={activeTab === p}
              className={`av-tab ${activeTab === p ? 'active' : ''}`}
              onClick={() => setActiveTab(p)}
            >
              {p}
              <span className="av-tab-count">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Deepgram-only: STT model picker, above the voice catalogue */}
      {activeTab === 'deepgram' && <DeepgramSttPicker onToast={showToast} />}

      <div className="av-section">
        <h2>{activeTab} TTS voices</h2>

        <div className="av-table-scroll">
        <table className="av-table">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Voice ID</th>
              <th style={{ width: '16%' }}>Display name</th>
              <th style={{ width: '8%' }}>Language</th>
              <th style={{ width: '8%' }}>Gender</th>
              <th style={{ width: '16%' }}>Region</th>
              <th style={{ width: '10%' }}>Source</th>
              <th style={{ width: '18%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {voices.map(v => {
              const dirty = isDirty(activeTab, v.id);
              const isAddition = v.source === 'addition';
              const previewKey = editKey(activeTab, v.id);
              return (
                <tr key={v.id} className={v.hidden ? 'hidden-row' : ''}>
                  <td><span className="av-id">{v.id}</span></td>
                  <td>
                    <input
                      className={`av-input ${dirty ? 'dirty' : ''}`}
                      value={valueFor(activeTab, v, 'name')}
                      onChange={e => onFieldChange(activeTab, v, 'display_name', e.target.value)}
                    />
                  </td>
                  <td>
                    {isAddition ? (
                      <input
                        className={`av-input ${dirty ? 'dirty' : ''}`}
                        value={valueFor(activeTab, v, 'language')}
                        onChange={e => onFieldChange(activeTab, v, 'language', e.target.value)}
                      />
                    ) : (
                      <span style={{ color: '#666' }}>{v.language}</span>
                    )}
                  </td>
                  <td>
                    {isAddition ? (
                      <select
                        className={`av-input ${dirty ? 'dirty' : ''}`}
                        value={valueFor(activeTab, v, 'gender')}
                        onChange={e => onFieldChange(activeTab, v, 'gender', e.target.value)}
                      >
                        {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    ) : (
                      <span style={{ color: '#666' }}>{v.gender}</span>
                    )}
                  </td>
                  <td>
                    <input
                      className={`av-input ${dirty ? 'dirty' : ''}`}
                      value={valueFor(activeTab, v, 'region')}
                      onChange={e => onFieldChange(activeTab, v, 'region', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    <span className={`av-source-pill ${v.source}`}>{v.source}</span>
                    {v.hidden && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#888' }}>hidden</span>}
                  </td>
                  <td>
                    <div className="av-actions">
                      <button
                        className="av-btn av-btn-primary"
                        disabled={!dirty || busy}
                        onClick={() => onSave(activeTab, v)}
                      >
                        Save
                      </button>
                      <button
                        className="av-btn"
                        disabled={busy || previewing === previewKey}
                        onClick={() => onPreview(activeTab, v)}
                      >
                        {previewing === previewKey ? 'Playing…' : 'Preview'}
                      </button>
                      {!isAddition && (
                        <button
                          className="av-btn"
                          disabled={busy}
                          onClick={() => onToggleHidden(activeTab, v)}
                        >
                          {v.hidden ? 'Unhide' : 'Hide'}
                        </button>
                      )}
                      {isAddition && (
                        <button
                          className="av-btn av-btn-danger"
                          disabled={busy}
                          onClick={() => onDelete(activeTab, v)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        <div className="av-add">
          <h3>Add new {activeTab} voice</h3>
          <div className="av-add-grid">
            <div className="av-add-field">
              <label>Voice ID</label>
              <input
                className="av-input"
                placeholder="aura-2-sander-nl"
                value={form.voice_id}
                onChange={e => setAdding(prev => ({ ...prev, [activeTab]: { ...form, voice_id: e.target.value } }))}
              />
            </div>
            <div className="av-add-field">
              <label>Display name</label>
              <input
                className="av-input"
                placeholder="Sander"
                value={form.display_name}
                onChange={e => setAdding(prev => ({ ...prev, [activeTab]: { ...form, display_name: e.target.value } }))}
              />
            </div>
            <div className="av-add-field">
              <label>Language</label>
              <input
                className="av-input"
                placeholder="nl"
                value={form.language}
                onChange={e => setAdding(prev => ({ ...prev, [activeTab]: { ...form, language: e.target.value } }))}
              />
            </div>
            <div className="av-add-field">
              <label>Gender</label>
              <select
                className="av-input"
                value={form.gender}
                onChange={e => setAdding(prev => ({ ...prev, [activeTab]: { ...form, gender: e.target.value } }))}
              >
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="av-add-field">
              <label>Region</label>
              <input
                className="av-input"
                placeholder="(optional)"
                value={form.region}
                onChange={e => setAdding(prev => ({ ...prev, [activeTab]: { ...form, region: e.target.value } }))}
              />
            </div>
            <div className="av-add-field">
              <label>&nbsp;</label>
              <button className="av-btn av-btn-primary" disabled={busy} onClick={() => onAdd(activeTab)}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      {feedback && (
        <div className={`av-toast ${feedback.type}`}>{feedback.msg}</div>
      )}
    </AdminLayout>
  );
}
